import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Controller, useForm } from 'react-hook-form';
import { nameValidation, requiredValidation, usPhoneNumberValidation } from 'validators';
import { Page, Button, TextField, PasswordField, Card, USPhoneNumberInput, TextButton } from 'seagull';
import ChangeEmailAddressModal from '~/components/ChangeEmailAddressModal/ChangeEmailAddressModal';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { usePasswordStrength } from '~/hooks/usePasswordStrength';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { validatePassword } from '~/utils/password-policy';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './Profile.module.scss';
const testPrefix = 'profile';
const nullIfEmpty = (s)=>s || null;
export default function Profile() {
    const overlayContext = useOverlayContext();
    const userInfo = useStore((state)=>state.userInfo);
    const isViewMode = useStore(selectors.isViewMode);
    const { setUserInfo, logBI, api } = useStoreActions();
    const { control, formState, handleSubmit, watch, trigger, clearErrors, reset } = useForm({
        mode: 'onChange'
    });
    const userLoggedInViaLink = userInfo === null || userInfo === void 0 ? void 0 : userInfo.loggedInViaLink();
    const userHasPassword = userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasPassword();
    const newPasswordValue = watch('newPassword', '');
    const newPasswordUserInputs = [
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName()
    ].filter(Boolean);
    const newPasswordStrengthScore = usePasswordStrength(newPasswordValue !== null && newPasswordValue !== void 0 ? newPasswordValue : '', newPasswordUserInputs);
    const { t: profileTexts } = useLocaleKeys('pages.profile');
    const firstNameValidator = nameValidation('First name', {
        required: profileTexts.validation.firstNameRequired()
    });
    const lastNameValidator = nameValidation('Last name', {
        required: profileTexts.validation.lastNameRequired()
    });
    const currentPasswordValidator = requiredValidation(!!watch('newPassword') && profileTexts.validation.currentPasswordRequired());
    const newPasswordValidator = {
        validate (password) {
            const hasPassword = !!password;
            hasPassword ? trigger('currentPassword') : clearErrors('currentPassword');
            if (!hasPassword) return true;
            return validatePassword(password, [
                userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
                userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
                userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName()
            ].filter(Boolean));
        }
    };
    const confirmPasswordValidator = {
        validate (password) {
            return password === watch('newPassword') || profileTexts.validation.passwordsMustMatch();
        }
    };
    useSetDocumentTitle(profileTexts.title());
    const onEditUserEmail = ()=>{
        overlayContext.showModal({
            biName: 'change_email_address_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ChangeEmailAddressModal, {
                    onClose: dismissModal
                });
            }
        });
    };
    const onSubmit = async (form)=>{
        logBI(10410);
        const updatedUserInfo = await handleRequest(api.auth.editUserDetails({
            name: {
                first: form.firstName,
                last: form.lastName
            },
            password: nullIfEmpty(form.newPassword),
            currentPassword: form.newPassword ? nullIfEmpty(form.currentPassword) : null,
            phone: nullIfEmpty(form.phone),
            clearPhoneNumber: !form.phone
        }));
        if (updatedUserInfo.success) {
            setUserInfo((userInfo === null || userInfo === void 0 ? void 0 : userInfo.updateUserDetails(updatedUserInfo.data)) || null);
            reset({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            overlayContext.showSuccessToast(profileTexts.changesSaved());
        } else {
            overlayContext.showErrorToast(updatedUserInfo.error.updateUserDetailsErrorText());
        }
    };
    return /*#__PURE__*/ _jsx(Page, {
        header: profileTexts.title(),
        skin: "secondary",
        testId: "".concat(testPrefix, "-page"),
        actions: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'save_profile_changes'
            },
            intent: Intent.UpdateProfile,
            type: "submit",
            form: testPrefix,
            loading: formState.isSubmitting,
            testId: "".concat(testPrefix, "-btn-save-changes"),
            children: profileTexts.saveChanges()
        }),
        children: /*#__PURE__*/ _jsx("form", {
            id: testPrefix,
            onSubmit: handleSubmit(onSubmit),
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.root,
                children: [
                    /*#__PURE__*/ _jsxs(Section, {
                        title: profileTexts.personalDetails.title(),
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "firstName",
                                defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
                                rules: firstNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_first_name'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.personalDetails.firstName(),
                                        testId: "".concat(testPrefix, "-first-name"),
                                        disabled: isViewMode
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "lastName",
                                defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName(),
                                rules: lastNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_last_name'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.personalDetails.lastName(),
                                        testId: "".concat(testPrefix, "-last-name"),
                                        disabled: isViewMode
                                    }))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.emailContainer,
                                children: [
                                    /*#__PURE__*/ _jsx(TextField, {
                                        bi: {
                                            name: 'profile_email'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field,
                                        value: userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
                                        label: profileTexts.personalDetails.emailAddress(),
                                        type: "email",
                                        testId: "".concat(testPrefix, "-email"),
                                        disabled: true
                                    }),
                                    /*#__PURE__*/ _jsx(TextButton, {
                                        bi: {
                                            name: 'edit_personal_details'
                                        },
                                        onClick: onEditUserEmail,
                                        underline: "none",
                                        testId: "".concat(testPrefix, "-email-edit-button"),
                                        type: "button",
                                        disabled: isViewMode,
                                        children: profileTexts.personalDetails.edit()
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "phone",
                                defaultValue: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.phone()) || undefined,
                                rules: usPhoneNumberValidation(),
                                render: (renderProps)=>/*#__PURE__*/ _jsx(USPhoneNumberInput, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_phone'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.personalDetails.phoneNumber(),
                                        testId: "".concat(testPrefix, "-phone"),
                                        disabled: isViewMode
                                    }))
                            })
                        ]
                    }),
                    !isViewMode && (userHasPassword || userLoggedInViaLink) && /*#__PURE__*/ _jsxs(Section, {
                        title: profileTexts.password.title(),
                        children: [
                            !userLoggedInViaLink && /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "currentPassword",
                                rules: currentPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_current_password'
                                        },
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.password.currentPassword(),
                                        testId: "".concat(testPrefix, "-current-password")
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "newPassword",
                                rules: newPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_new_password'
                                        },
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        showStrengthMeter: true,
                                        strengthScore: newPasswordStrengthScore,
                                        label: profileTexts.password.newPassword(),
                                        testId: "".concat(testPrefix, "-new-password")
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "confirmPassword",
                                rules: confirmPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_confirm_password'
                                        },
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.password.confirmPassword(),
                                        testId: "".concat(testPrefix, "-confirm-password")
                                    }))
                            })
                        ]
                    })
                ]
            })
        })
    });
}
const Section = (props)=>{
    return /*#__PURE__*/ _jsx(Card, {
        bi: {
            name: 'profile_section_card',
            data: {
                section: props.title
            }
        },
        padding: "m",
        autoHeight: true,
        title: props.title,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.content,
            children: props.children
        })
    });
};
